import { Check } from "lucide-react";
import { useState } from "react";

import { Accent, Monogram, Section, displayTitle } from "@/features/platform/components/landing-ui";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/** How the shared schedule looks for each size of business. Sample trainers. */
const SIZES = [
  {
    id: "solo",
    name: () => m.platform_team_solo(),
    lines: () => [m.platform_team_solo_one(), m.platform_team_solo_two(), m.platform_team_solo_three()],
    trainers: ["Kasia Nowak"],
  },
  {
    id: "studio",
    name: () => m.platform_team_studio(),
    lines: () => [m.platform_team_studio_one(), m.platform_team_studio_two(), m.platform_team_studio_three()],
    trainers: ["Kasia Nowak", "Marek Wójcik", "Ania Lis"],
  },
  {
    id: "club",
    name: () => m.platform_team_club(),
    lines: () => [m.platform_team_club_one(), m.platform_team_club_two(), m.platform_team_club_three()],
    trainers: ["Kasia Nowak", "Marek Wójcik", "Ania Lis", "Tomek Zając", "Ola Kamińska", "Piotr Baran"],
  },
];

/** Which hours each column fills, so the drawing reads as a real, uneven week. */
const BUSY = [
  [0, 1, 3],
  [1, 2],
  [0, 2, 3],
  [1, 3],
  [0, 1],
  [2, 3],
];

/**
 * One trainer or a whole club: pick a size and the shared schedule redraws with a column per
 * trainer, beside what changes for that size.
 */
export const TeamSize = () => {
  const [sizeId, setSizeId] = useState(SIZES[0]?.id);
  const size = SIZES.find((entry) => entry.id === sizeId) ?? SIZES[0];

  return (
    <Section id="team">
      <div className="grid items-center gap-10 lg:grid-cols-[1fr_1.1fr] lg:gap-14">
        <div className="px-1">
          <h2 className={cn(displayTitle, "text-[clamp(2.5rem,5.4vw,5rem)] leading-none")}>
            {m.platform_team_title_lead()} <Accent>{m.platform_team_title_tail()}</Accent>
          </h2>
          <p className="mt-5 max-w-[460px] text-lg leading-[1.55] text-muted-foreground">{m.platform_team_body()}</p>
          <div role="group" aria-label={m.platform_team_pick()} className="mt-8 flex flex-wrap gap-2">
            {SIZES.map((entry) => (
              <button
                key={entry.id}
                type="button"
                aria-pressed={entry.id === size?.id}
                className="h-10 rounded-full border border-border px-[18px] text-sm text-muted-foreground transition-colors hover:text-foreground aria-pressed:border-foreground aria-pressed:bg-foreground aria-pressed:font-semibold aria-pressed:text-background"
                onClick={() => setSizeId(entry.id)}
              >
                {entry.name()}
              </button>
            ))}
          </div>
          <ul aria-live="polite" className="mt-6 grid gap-2.5">
            {size?.lines().map((line) => (
              <li key={line} className="flex items-start gap-2.5 text-[15px]">
                <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground">
                  <Check className="size-3" strokeWidth={3} />
                </span>
                {line}
              </li>
            ))}
          </ul>
        </div>

        <div aria-hidden className="rounded-3xl border border-border p-4">
          <div className="flex items-center justify-between px-1 pb-3 text-[13px]">
            <b className="font-semibold">{m.platform_team_schedule()}</b>
            <span className="text-muted-foreground">{m.platform_team_no_double()}</span>
          </div>
          <div
            className="grid gap-2"
            style={{ gridTemplateColumns: `repeat(${size?.trainers.length ?? 1}, minmax(0, 1fr))` }}
          >
            {size?.trainers.map((trainer, column) => (
              <div key={trainer} className="min-w-0 rounded-2xl bg-[var(--site-well)] p-2">
                <div className="flex items-center gap-2 px-0.5 pb-2">
                  <Monogram name={trainer} className="size-7 rounded-lg text-[10px]" />
                  {size.trainers.length < 4 ? <span className="truncate text-xs font-medium">{trainer}</span> : null}
                </div>
                <div className="grid gap-1.5">
                  {[0, 1, 2, 3].map((slot) => (
                    <span
                      key={slot}
                      className={cn(
                        "h-9 rounded-lg",
                        BUSY[column]?.includes(slot)
                          ? column === 0
                            ? "bg-primary"
                            : "bg-foreground/80"
                          : "border border-dashed border-border",
                      )}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
};
